/**
 * Badge Utility Functions
 *
 * Shared utilities for badge display across components.
 */

/**
 * Badge image mapping by badge ID
 */
export const BADGE_IMAGES = {
  first_signal: require('../assets/images/badge-first-signal.png'),
  consistent_observer: require('../assets/images/badge-consistent-observer.png'),
  safety_contributor: require('../assets/images/badge-safety-contributor.png'),
  dedicated_advocate: require('../assets/images/badge-dedicated-advocate.png'),
  culture_builder: require('../assets/images/badge-culture-builder.png'),
};

/**
 * Placeholder image for locked/mystery badges
 */
export const BADGE_PLACEHOLDER = require('../assets/images/badge-surprise.png');

/**
 * Get badge image by badge ID
 * @param {string} badgeId - Badge ID (e.g., 'first_signal', 'consistent_observer')
 * @returns {number|null} Image require or null if not found
 */
export function getBadgeImage(badgeId) {
  return BADGE_IMAGES[badgeId] || null;
}

/**
 * Get placeholder image for locked badges
 * @returns {number} Placeholder image require
 */
export function getBadgePlaceholder() {
  return BADGE_PLACEHOLDER;
}

/**
 * Badge color mapping for consistent styling
 * Muted, warm tones that feel professional
 */
export const BADGE_COLORS = {
  gold: {
    background: 'rgba(255, 196, 81, 0.15)',
    border: 'rgba(255, 196, 81, 0.4)',
    icon: '#D4A84B',
  },
  blue: {
    background: 'rgba(59, 130, 246, 0.12)',
    border: 'rgba(59, 130, 246, 0.3)',
    icon: '#3B82F6',
  },
  green: {
    background: 'rgba(34, 197, 94, 0.12)',
    border: 'rgba(34, 197, 94, 0.3)',
    icon: '#22C55E',
  },
  purple: {
    background: 'rgba(139, 92, 246, 0.12)',
    border: 'rgba(139, 92, 246, 0.3)',
    icon: '#8B5CF6',
  },
  teal: {
    background: 'rgba(20, 184, 166, 0.12)',
    border: 'rgba(20, 184, 166, 0.3)',
    icon: '#14B8A6',
  },
};

export const LOCKED_COLORS = {
  background: '#F5F5F5',
  border: '#E5E5E5',
  icon: '#D1D5DB',
};

/**
 * Get badge emoji/symbol based on color
 * @param {string} color - Badge color (gold, blue, green, purple, teal)
 * @returns {string} Unicode symbol for the badge
 */
export function getBadgeEmoji(color) {
  switch (color) {
    case 'gold':
      return '\u2605'; // Star
    case 'blue':
      return '\u25C9'; // Circle with dot
    case 'green':
      return '\u2713'; // Checkmark
    case 'purple':
      return '\u2666'; // Diamond
    case 'teal':
      return '\u2726'; // Four-pointed star
    default:
      return '\u2605';
  }
}

/**
 * Get colors for a badge based on earned state
 * @param {string} color - Badge color theme
 * @param {boolean} earned - Whether badge is earned
 * @returns {Object} Color object with background, border, icon
 */
export function getBadgeColors(color, earned) {
  if (!earned) {
    return LOCKED_COLORS;
  }
  return BADGE_COLORS[color] || BADGE_COLORS.gold;
}
